import type { DocumentTreeItem } from "@exopengithub/exokit";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useGetMyDocumentsQuery } from "src/features/excelReports/api/excelReportsApi.js";
import { useOpenExcelReport } from "src/features/excelReports/hooks/useOpenExcelReport.js";
import {
	isEox7Forbidden,
	reportEox7Error,
} from "src/features/excelReports/utils/reportEox7Error.js";
import { getReportOpenMode } from "src/features/excelReports/utils/reportOpenMode.js";
import { toReportTree } from "src/features/excelReports/utils/reportTreeItems.js";
import { getProduct } from "src/features/products/products.js";
import { useLoadedCompanyDomain } from "src/routes/routeDataHooks.js";
import {
	ExcelReportsWidgetView,
	type ExcelReportsWidgetStatus,
} from "./ExcelReportsWidgetView.js";

const toReportFile = (item: DocumentTreeItem) => ({
	id: Number(item.id),
	name: item.name,
});

export const ExcelReportsWidgetContent = ({
	companyDomainId,
	initialQuery,
	action,
	growWithContent,
}: {
	companyDomainId: string;
	initialQuery?: string;
	action?: ReactNode;
	growWithContent?: boolean;
}) => {
	const { data, error, isLoading } = useGetMyDocumentsQuery(companyDomainId);
	const { openReport, isOpening, getDisabledReason } =
		useOpenExcelReport(companyDomainId);
	const [showLaunchNote, setShowLaunchNote] = useState(false);

	useEffect(() => {
		if (error && !isEox7Forbidden(error))
			reportEox7Error(error, "documents/me");
	}, [error]);

	const tree = useMemo(
		() => (data ? toReportTree(data.documents) : undefined),
		[data],
	);

	const getStatus = (): ExcelReportsWidgetStatus => {
		if (isLoading) return "loading";
		// A 403 means eox7 won't make the caller a report user in this domain.
		if (isEox7Forbidden(error)) return "no-access";
		if (error || !data) return "error";
		// Documents exist, but none are shared with the caller.
		if (data.totalDocuments > 0 && data.documents.length === 0) {
			return "no-access";
		}
		// Shared documents outside Reports → Documents still count as an empty report tree.
		return "ready";
	};

	const handleOpenFile = async (item: DocumentTreeItem) => {
		const file = toReportFile(item);
		const opened = await openReport(file);
		if (opened && getReportOpenMode(file.name) === "excel") {
			setShowLaunchNote(true);
		}
	};

	return (
		<ExcelReportsWidgetView
			status={getStatus()}
			items={tree?.items ?? []}
			rootId={tree?.rootId ?? undefined}
			onOpenFile={(item) => void handleOpenFile(item)}
			getItemStatus={(item) => {
				if (item.type === "folder") return {};
				const file = toReportFile(item);
				return {
					busy: isOpening(file.id),
					disabledReason: getDisabledReason(file) ?? undefined,
				};
			}}
			showLaunchNote={showLaunchNote}
			onDismissLaunchNote={() => setShowLaunchNote(false)}
			initialQuery={initialQuery}
			action={action}
			growWithContent={growWithContent}
		/>
	);
};

export const ExcelReportsWidget = () => {
	const { t } = useTranslation();
	const companyDomain = useLoadedCompanyDomain();
	// WHY keyed: the dashboard stays mounted across a domain switch, and the query would keep showing the previous domain's reports while the new ones load.
	return (
		<ExcelReportsWidgetContent
			key={companyDomain.id}
			companyDomainId={companyDomain.id}
			action={
				<Link
					to={getProduct("excel").path(companyDomain)}
					className="hover:underline"
				>
					{t("See all")} →
				</Link>
			}
		/>
	);
};
